@if (enterpriseNotice()) {
  <section class="billing-section">
    <article class="billing-contact-card">
      <span class="billing-contact-card__icon" aria-hidden="true">
        <i class="pi pi-envelope"></i>
      </span>
      <div class="billing-contact-card__body">
        <h4 class="billing-contact-card__title">
          {{ 'SETTINGS_BILLING.PROFILE.ENTERPRISE_TITLE' | translate }}
        </h4>
        <p class="billing-contact-card__desc">
          {{ 'SETTINGS_BILLING.PLAN.CONTACT.DESCRIPTION_PREFIX' | translate }}
          <a class="billing-link" [href]="'mailto:' + enterpriseContactEmail()">
            {{ enterpriseContactEmail() }}
          </a>
          {{ 'SETTINGS_BILLING.PLAN.CONTACT.DESCRIPTION_SUFFIX' | translate }}
        </p>
      </div>
      <div class="billing-contact-card__cta">
        <p-button
          [label]="'SETTINGS_BILLING.PLAN.CONTACT.BUTTON' | translate"
          (onClick)="contactSalesRequested.emit()" />
      </div>
    </article>
  </section>
} @else if (showInitialError()) {
  <tf-settings-load-error
    [title]="'SETTINGS_BILLING.ERROR.LOAD_FAILED_TITLE' | translate"
    [message]="'SETTINGS_BILLING.ERROR.LOAD_FAILED_DESCRIPTION' | translate"
    [details]="loadErrorDetails()"
    [dense]="true"
    [retrying]="isLoading()"
    (retry)="refresh()" />
} @else if (showInitialLoading()) {
  <section class="billing-section billing-profile-skeleton" aria-hidden="true">
    <div class="billing-skeleton-card billing-skeleton-card--profile">
      <span class="billing-skeleton-bar billing-skeleton-bar--title"></span>
      <span class="billing-skeleton-bar billing-skeleton-bar--description"></span>
      <div class="billing-profile-skeleton__grid">
        @for (_ of profileSkeletonFields; track _) {
          <div class="billing-profile-skeleton__field">
            <span class="billing-skeleton-bar billing-skeleton-bar--label"></span>
            <span class="billing-skeleton-input"></span>
          </div>
        }
      </div>
      <span class="billing-skeleton-button billing-skeleton-button--right"></span>
    </div>

    <div class="billing-skeleton-card billing-skeleton-card--payment">
      <span class="billing-skeleton-bar billing-skeleton-bar--title"></span>
      <div class="billing-profile-skeleton__payment-row">
        <span class="billing-skeleton-icon"></span>
        <div>
          <span class="billing-skeleton-bar billing-skeleton-bar--medium"></span>
          <span class="billing-skeleton-bar billing-skeleton-bar--short"></span>
        </div>
        <span class="billing-skeleton-button"></span>
      </div>
    </div>
  </section>
} @else {
  <section class="billing-section">
    <tf-settings-inline-message
      [message]="
        showReloadError() ? ('SETTINGS_BILLING.ERROR.LOAD_FAILED_DESCRIPTION' | translate) : null
      "
      messageId="billing-profile-reload-error"
      (dismissed)="clearLoadError()" />

    <h3 class="settings-page__section-title">
      {{ 'SETTINGS_BILLING.PROFILE.TITLE' | translate }}
    </h3>
    <p class="settings-page__description">
      {{ 'SETTINGS_BILLING.PROFILE.DESCRIPTION' | translate }}
    </p>

    <article class="billing-profile-card">
      <div class="billing-profile-grid">
        <div class="form-field">
          <label for="billingFullName">
            {{ 'SETTINGS_BILLING.PROFILE.FIELD.FULL_NAME' | translate }}
            <span class="required" aria-hidden="true">*</span>
          </label>
          <input
            pInputText
            id="billingFullName"
            type="text"
            [formField]="profileForm.billingFullName"
            class="w-full" />
          @if (profileForm.billingFullName().touched() && profileForm.billingFullName().invalid()) {
            <small class="p-error">
              {{ profileForm.billingFullName().errors()[0]?.message | translate }}
            </small>
          }
        </div>

        <div class="form-field">
          <label for="billingBusinessName">
            {{ 'SETTINGS_BILLING.PROFILE.FIELD.COMPANY_NAME' | translate }}
          </label>
          <input
            pInputText
            id="billingBusinessName"
            type="text"
            [formField]="profileForm.billingBusinessName"
            class="w-full" />
        </div>

        @let emailField = profileForm.billingEmail();
        @let emailInteracted = emailField.touched() || emailField.dirty();
        @let emailShowError = emailInteracted && emailField.invalid();
        <div class="form-field form-field--span-2">
          <label for="billingEmail">
            {{ 'SETTINGS_BILLING.PROFILE.FIELD.BILLING_EMAIL' | translate }}
            <span class="required" aria-hidden="true">*</span>
          </label>
          <input
            pInputText
            id="billingEmail"
            type="email"
            inputmode="email"
            autocomplete="email"
            [formField]="profileForm.billingEmail"
            [attr.aria-invalid]="emailShowError || null"
            [attr.aria-describedby]="emailShowError ? 'billingEmail-error' : 'billingEmail-hint'"
            class="w-full" />
          <small id="billingEmail-hint" class="form-field__hint">
            {{ 'SETTINGS_BILLING.PROFILE.FIELD.BILLING_EMAIL_HINT' | translate }}
          </small>
          @if (emailShowError) {
            <small id="billingEmail-error" class="p-error" aria-live="polite">
              {{ (emailField.errors()[0]?.message
                    ?? 'SETTINGS_BILLING.PROFILE.VALIDATION.EMAIL_INVALID'
                  ) | translate }}
            </small>
          }
        </div>

        @let addressLine1Field = profileForm.billingAddressLine1();
        @let addressLine1ShowError = addressLine1Field.touched() && addressLine1Field.invalid();
        <div class="form-field form-field--span-2">
          <label for="billingAddressLine1">
            {{ 'SETTINGS_BILLING.PROFILE.FIELD.ADDRESS_LINE1' | translate }}
            <span class="required" aria-hidden="true">*</span>
          </label>
          <input
            pInputText
            id="billingAddressLine1"
            type="text"
            autocomplete="address-line1"
            [formField]="profileForm.billingAddressLine1"
            [attr.aria-invalid]="addressLine1ShowError || null"
            [attr.aria-describedby]="addressLine1ShowError ? 'billingAddressLine1-error' : null"
            class="w-full" />
          @if (addressLine1ShowError) {
            <small id="billingAddressLine1-error" class="p-error" aria-live="polite">
              {{ addressLine1Field.errors()[0]?.message | translate }}
            </small>
          }
        </div>

        <div class="form-field form-field--span-2">
          <label for="billingAddressLine2">
            {{ 'SETTINGS_BILLING.PROFILE.FIELD.ADDRESS_LINE2' | translate }}
          </label>
          <input
            pInputText
            id="billingAddressLine2"
            type="text"
            autocomplete="address-line2"
            [formField]="profileForm.billingAddressLine2"
            class="w-full" />
        </div>

        @let postalCodeField = profileForm.billingPostalCode();
        @let postalCodeShowError = postalCodeField.touched() && postalCodeField.invalid();
        <div class="form-field">
          <label for="billingPostalCode">
            {{ 'SETTINGS_BILLING.PROFILE.FIELD.POSTAL_CODE' | translate }}
            <span class="required" aria-hidden="true">*</span>
          </label>
          <input
            pInputText
            id="billingPostalCode"
            type="text"
            autocomplete="postal-code"
            [formField]="profileForm.billingPostalCode"
            [attr.aria-invalid]="postalCodeShowError || null"
            [attr.aria-describedby]="postalCodeShowError ? 'billingPostalCode-error' : null"
            class="w-full" />
          @if (postalCodeShowError) {
            <small id="billingPostalCode-error" class="p-error" aria-live="polite">
              {{ postalCodeField.errors()[0]?.message | translate }}
            </small>
          }
        </div>

        @let cityField = profileForm.billingCity();
        @let cityShowError = cityField.touched() && cityField.invalid();
        <div class="form-field">
          <label for="billingCity">
            {{ 'SETTINGS_BILLING.PROFILE.FIELD.CITY' | translate }}
            <span class="required" aria-hidden="true">*</span>
          </label>
          <input
            pInputText
            id="billingCity"
            type="text"
            autocomplete="address-level2"
            [formField]="profileForm.billingCity"
            [attr.aria-invalid]="cityShowError || null"
            [attr.aria-describedby]="cityShowError ? 'billingCity-error' : null"
            class="w-full" />
          @if (cityShowError) {
            <small id="billingCity-error" class="p-error" aria-live="polite">
              {{ cityField.errors()[0]?.message | translate }}
            </small>
          }
        </div>

        <div class="form-field">
          <label for="billingStateProvince">
            {{ 'SETTINGS_BILLING.PROFILE.FIELD.STATE' | translate }}
          </label>
          <input
            pInputText
            id="billingStateProvince"
            type="text"
            autocomplete="address-level1"
            [formField]="profileForm.billingStateProvince"
            class="w-full" />
        </div>

        <div class="form-field">
          <label for="billingCountryCodeRO">
            {{ 'SETTINGS_BILLING.PROFILE.FIELD.COUNTRY' | translate }}
          </label>
          <input
            pInputText
            id="billingCountryCodeRO"
            type="text"
            readonly
            [value]="readonlyCountry()"
            class="w-full" />
        </div>

        <div class="form-field">
          <label for="billingVatNumberRO">
            {{ 'SETTINGS_BILLING.PROFILE.FIELD.VAT_NUMBER' | translate }}
          </label>
          <input
            pInputText
            id="billingVatNumberRO"
            type="text"
            readonly
            [value]="readonlyVatNumber()"
            class="w-full" />
        </div>

        <p class="form-field--span-2 form-field__hint billing-profile-readonly-hint">
          <i class="pi pi-info-circle" aria-hidden="true"></i>
          {{ 'SETTINGS_BILLING.PROFILE.READ_ONLY_HINT' | translate }}
        </p>
      </div>

      <tf-settings-inline-message
        [message]="saveFailed() ? ('SETTINGS_BILLING.PROFILE.SAVE_FAILED' | translate) : null"
        messageId="billing-profile-save-error"
        (dismissed)="clearSaveError()" />

      <div class="billing-profile-actions">
        <p-button
          [label]="'SETTINGS_BILLING.PROFILE.SAVE' | translate"
          severity="primary"
          [disabled]="profileForm().invalid() || isSaving()"
          [loading]="isSaving()"
          (onClick)="onSave()" />
      </div>
    </article>

    @if (showPaymentMethod()) {
      <article class="billing-payment-method-card">
        <header class="billing-payment-method-card__head">
          <h3>{{ 'SETTINGS_BILLING.PROFILE.PAYMENT_METHOD.TITLE' | translate }}</h3>
        </header>

        <tf-settings-inline-message
          [message]="
            paymentMethodActionFailed()
              ? ('SETTINGS_BILLING.PROFILE.PAYMENT_METHOD.OPEN_FAILED_DETAIL' | translate)
              : null
          "
          messageId="billing-payment-method-action-error"
          (dismissed)="clearPaymentMethodActionError()" />

        @if (billingCustomerMissing()) {
          <p class="settings-page__description">
            {{ 'SETTINGS_BILLING.PROFILE.PAYMENT_METHOD.NO_CUSTOMER' | translate }}
          </p>
        } @else if (isLoadingPaymentMethod()) {
          <div class="billing-payment-method-skeleton" aria-hidden="true">
            <span class="billing-skeleton-icon"></span>
            <div>
              <span class="billing-skeleton-bar billing-skeleton-bar--medium"></span>
              <span class="billing-skeleton-bar billing-skeleton-bar--short"></span>
            </div>
            <span class="billing-skeleton-button"></span>
          </div>
        } @else if (paymentMethodLoadFailed()) {
          <tf-settings-inline-message
            [message]="'SETTINGS_BILLING.PROFILE.PAYMENT_METHOD.LOAD_FAILED' | translate"
            messageId="billing-payment-method-load-error"
            (dismissed)="clearPaymentMethodLoadError()" />
        } @else if (paymentMethodSummary(); as pm) {
          <div class="billing-payment-method-row">
            <span class="billing-payment-method-row__icon" aria-hidden="true">
              <i class="pi pi-credit-card"></i>
            </span>
            <div class="billing-payment-method-row__body">
              <p class="billing-payment-method-row__title">
                {{
                  'SETTINGS_BILLING.PROFILE.PAYMENT_METHOD.BRAND_LINE'
                    | translate: { brand: pm.brand, last4: pm.last4 }
                }}
              </p>
              <p class="billing-payment-method-row__sub">
                {{
                  'SETTINGS_BILLING.PROFILE.PAYMENT_METHOD.EXPIRES_LINE'
                    | translate: { expMonth: pm.expMonth, expYear: pm.expYear }
                }}
              </p>
            </div>
            <div class="billing-payment-method-row__cta">
              <p-button
                [label]="'SETTINGS_BILLING.PROFILE.PAYMENT_METHOD.UPDATE_BUTTON' | translate"
                icon="pi pi-external-link"
                severity="secondary"
                [outlined]="true"
                [loading]="isOpeningPortal()"
                [disabled]="isOpeningPortal()"
                (onClick)="openPaymentPortal()" />
            </div>
          </div>
        } @else {
          <div class="billing-payment-method-row billing-payment-method-row--empty">
            <span class="billing-payment-method-row__icon" aria-hidden="true">
              <i class="pi pi-credit-card"></i>
            </span>
            <div class="billing-payment-method-row__body">
              <p class="billing-payment-method-row__title">
                {{ 'SETTINGS_BILLING.PROFILE.PAYMENT_METHOD.EMPTY_TITLE' | translate }}
              </p>
              <p class="billing-payment-method-row__sub">
                {{ 'SETTINGS_BILLING.PROFILE.PAYMENT_METHOD.EMPTY_DESCRIPTION' | translate }}
              </p>
            </div>
            <div class="billing-payment-method-row__cta">
              <p-button
                [label]="'SETTINGS_BILLING.PROFILE.PAYMENT_METHOD.ADD_BUTTON' | translate"
                icon="pi pi-external-link"
                [loading]="isOpeningPortal()"
                [disabled]="isOpeningPortal()"
                (onClick)="openPaymentPortal()" />
            </div>
          </div>
        }
      </article>
    }
  </section>
}
